"use client";

import { useEffect } from "react";
import Image from "next/image";
import { X, ExternalLink } from "lucide-react";
import { Artwork } from "@/lib/data";

interface LightboxProps {
  artwork: Artwork | null;
  onClose: () => void;
}

export function Lightbox({ artwork, onClose }: LightboxProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    if (artwork) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [artwork]);

  if (!artwork) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm lightbox-enter"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full mx-4 bg-[#1a1a1a] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Image */}
        <div className="relative w-full aspect-square sm:aspect-[4/3] max-h-[70vh]">
          <Image
            src={artwork.imageUrl}
            alt={artwork.title}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 800px"
            unoptimized
          />
        </div>

        {/* Info */}
        <div className="p-6 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl font-semibold text-white">
              {artwork.title}
            </h3>
            <p className="text-sm text-[#c9a84c] mt-1 capitalize">
              {artwork.category}
            </p>
          </div>
          <a
            href={artwork.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#c9a84c] text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#f5d78e] transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Shop on Pixels
          </a>
        </div>
      </div>
    </div>
  );
}
